import { isNative } from "@/lib/native";
import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, RotateCcw, Lock, Bookmark, BookmarkCheck } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import ShareAnswerButton from "@/components/ShareAnswerButton";
import { api, API } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const WIDE = !isNative;

const SUGGESTIONS = [
  "What does today's sky mean for my love life?",
  "How does my Moon sign shape my emotions?",
  "What should I focus on this month?",
];

export function Bubble({ m, i, saved, onSave }) {
  const mine = m.role === "user";
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
      <div
        data-testid={`chat-msg-${i}`}
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-base leading-relaxed ${
          mine ? "bg-[#D4AF37] text-[#04060B] rounded-br-md" : "bg-[#0F1523] border border-[#D4AF37]/20 text-[#D6D0C5] rounded-bl-md"
        }`}
      >
        {m.content?.trimEnd() || <Loader2 size={16} className="animate-spin gold-text" />}
      </div>
      {onSave && m.id && (
        <div className="flex items-center gap-4">
        <button
          data-testid={`chat-save-${i}`}
          onClick={() => !saved && onSave(m.id)}
          className={`mt-1 ml-1 py-2 flex items-center gap-1 text-xs transition-colors ${saved ? "gold-text" : "text-[#6c7686] hover:text-[#D4AF37]"}`}
        >
          {saved ? <BookmarkCheck size={13} /> : <Bookmark size={13} />} {saved ? "Saved to Archive" : "Save to Archive"}
        </button>
        <ShareAnswerButton messageId={m.id} testId={`chat-share-${i}`} className="mt-1 py-2" />
        </div>
      )}
    </div>
  );
}

export async function streamReply(path, payload, onDelta, onFollowups, onDone) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.detail || "Something went wrong"), { status: res.status });
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n\n");
    buf = lines.pop();
    for (const l of lines) {
      if (!l.startsWith("data: ")) continue;
      const ev = JSON.parse(l.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
      if (ev.followups) onFollowups(ev.followups);
      if (ev.done && ev.id) onDone(ev.id);
    }
  }
}

export default function Ask({ onSubscribe }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState(null);
  const [quota, setQuota] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedIds, setSavedIds] = useState(new Set());
  const [buying, setBuying] = useState(false);
  const [params, setParams] = useSearchParams();
  const { currency, prices } = useCurrency();
  const endRef = useRef(null);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    document.body.classList.toggle("kb-open", typing);
    return () => document.body.classList.remove("kb-open");
  }, [typing]);

  const load = () => api.get("/chat").then(({ data }) => { setMessages(data.messages); setQuota(data.quota); });
  useEffect(() => {
    load();
    api.get("/chat/saved-ids").then(({ data }) => setSavedIds(new Set(data.ids))).catch(() => {});
    const q = params.get("q");
    if (q) {
      setInput(q);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (id) => {
    try {
      await api.post("/chat/save", { message_id: id });
      setSavedIds((p) => new Set(p).add(id));
      toast.success("Saved to your Archive");
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Couldn't save right now");
    }
  };

  const buyPack = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "chat_pack", currency, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setBuying(false);
    }
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const demo = !!user?.is_demo;
  const out = demo || (quota && !quota.premium && quota.remaining <= 0);

  const send = async (text) => {
    if (out) return onSubscribe("unlimited Astra Interactive questions");
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setError("");
    setInput("");
    setBusy(true);
    setMessages((p) => [...p, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamReply(
        "/chat/stream",
        { message: msg },
        (d) => setMessages((p) => [...p.slice(0, -1), { ...p[p.length - 1], content: p[p.length - 1].content + d }]),
        (f) => setMessages((p) => [...p.slice(0, -1), { ...p[p.length - 1], followups: f }]),
        (id) => setMessages((p) => [...p.slice(0, -1), { ...p[p.length - 1], id }]),
      );
      if (!quota.premium) setQuota((q) => ({ ...q, remaining: q.remaining - 1 }));
    } catch (e) {
      setMessages((p) => p.slice(0, -2));
      setInput(msg);
      if (e.status === 402) onSubscribe("unlimited Astra Interactive questions");
      else setError(e.message);
      load();
    }
    setBusy(false);
  };

  const reset = async () => {
    await api.post("/chat/new");
    setMessages([]);
  };

  const composer = (inline) => (
  <form
    onSubmit={(e) => { e.preventDefault(); send(); }}
    data-testid={inline ? "chat-composer-inline" : "chat-composer"}
    className={inline ? "mt-4 flex gap-2" : `fixed ${typing ? "bottom-0 pb-3" : "bottom-[calc(68px+env(safe-area-inset-bottom))] pb-3"} left-0 right-0 max-w-md mx-auto px-4 pt-3 bg-[#04060B]/90 backdrop-blur-xl border-t border-[#D4AF37]/15 flex gap-2 z-30 ${WIDE ? "lg:max-w-3xl lg:bottom-24 lg:pb-3 lg:px-8 lg:border-t-0 lg:bg-[#04060B]/70 lg:rounded-full" : ""}`}
  >
    <input
      data-testid="chat-input"
      value={input}
      onChange={(e) => setInput(e.target.value)}
      onFocus={() => setTyping(true)}
      onBlur={() => setTyping(false)}
      enterKeyHint="send"
      maxLength={1000}
      placeholder={out ? "Unlock Premium to keep asking" : "Ask the stars…"}
      className="flex-1 bg-[#0F1523] border border-[#D4AF37]/25 rounded-full px-4 py-3 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60"
    />
    <button data-testid="chat-send" type="submit" onMouseDown={(e) => e.preventDefault()} disabled={busy || (!out && !input.trim())}
      className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] disabled:opacity-50 transition-colors">
      {busy ? <Loader2 size={18} className="animate-spin" /> : out ? <Lock size={18} /> : <Send size={18} />}
    </button>
  </form>
  );

  return (
    <div className={`px-5 pt-5 pb-44 ${WIDE ? "lg:max-w-3xl lg:mx-auto lg:px-8 lg:pt-12 lg:pb-52" : ""}`} data-testid="ask-page">
      <div className="flex items-start justify-between mb-1">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Ask the stars</p>
          <h1 className={`text-3xl font-light mt-1 ${WIDE ? "lg:text-5xl lg:mb-3" : ""}`}>Astra Interactive</h1>
        </div>
        {messages?.length > 0 && (
          <button data-testid="btn-chat-new" onClick={reset} disabled={busy} className="flex items-center gap-1 text-sm gold-text mt-2">
            <RotateCcw size={14} /> New chat
          </button>
        )}
      </div>
      {quota && !quota.premium && (
        <p data-testid="chat-quota" className="text-sm text-[#A7B0C0] mb-4">
          {demo
            ? "Create your free account to try 3 free questions."
            : out
            ? "You've used all your questions."
            : quota.credits > 0
            ? `${quota.remaining} questions left.`
            : `${quota.remaining} of ${quota.free_limit} free questions left.`}
          {" "}
          <button data-testid="btn-chat-premium" onClick={() => onSubscribe("unlimited Astra Interactive questions")} className="gold-text hover:underline">
            Unlock unlimited with Premium
          </button>
        </p>
      )}
      {out && !demo && !isNative && (
        <div data-testid="chat-pack-card" className="mb-4 bg-[#0F1523] border border-[#D4AF37]/35 rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-[0.25em] gold-text mb-1">Question pack</p>
          <p className="text-base text-[#D6D0C5] mb-3">Keep the conversation going: 10 more questions, one-off payment, never expire.</p>
          <button
            data-testid="btn-buy-chat-pack"
            onClick={buyPack}
            disabled={buying}
            className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors disabled:opacity-60"
          >
            {buying ? <Loader2 size={18} className="animate-spin" /> : `Get 10 questions · ${prices.symbol}${prices.chatPack}`}
          </button>
        </div>
      )}
      {!messages ? (
        <Loader2 className="animate-spin gold-text mx-auto my-20" size={26} />
      ) : messages.length === 0 ? (
        <div className="mt-6" data-testid="chat-empty">
          <p className="text-base text-[#D6D0C5] mb-4">
            Hello {user?.name?.split(" ")[0] || "stargazer"}, welcome to Astra Interactive. Ask the stars anything about your chart and what today's planets mean for you.
          </p>
          <div className={`space-y-2 ${WIDE ? "lg:grid lg:grid-cols-3 lg:gap-3 lg:space-y-0" : ""}`}>
            {SUGGESTIONS.map((s, i) => (
              <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)}
                className={`w-full text-left flex items-center gap-2 bg-[#0F1523] border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 rounded-2xl px-4 py-3 text-sm text-[#E8DCC4] transition-colors ${WIDE ? "lg:items-start lg:py-5 lg:text-base" : ""}`}>
                <Sparkles size={14} className={`gold-text shrink-0 ${WIDE ? "lg:mt-1" : ""}`} /> {s}
              </button>
            ))}
          </div>
          {composer(true)}
        </div>
      ) : (
        <div className="space-y-3 mt-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <Bubble key={i} m={m} i={i} saved={savedIds.has(m.id)} onSave={!demo && m.role === "assistant" && !(busy && i === messages.length - 1) ? save : null} />
          ))}
          {!busy && messages[messages.length - 1]?.followups?.length > 0 && (
            <div className="flex flex-col items-start gap-2 pt-1" data-testid="chat-followups">
              {messages[messages.length - 1].followups.map((f, i) => (
                <button key={f} data-testid={`chat-followup-${i}`} onClick={() => send(f)}
                  className="text-left flex items-center gap-2 border border-[#D4AF37]/35 hover:bg-[#D4AF37]/10 rounded-full px-4 py-2 text-sm text-[#E8DCC4] transition-colors">
                  <Sparkles size={13} className="gold-text shrink-0" /> {f}
                </button>
              ))}
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}
      {error && <p data-testid="chat-error" className="text-sm text-red-300 mt-3">{error}</p>}

      {messages?.length > 0 && composer(false)}
    </div>
  );
}
